// Enums
import { HOLDING_TYPE_ALLOCATION, DETAILED_ASSET_ALLOCATION, PORTFOLIO_ASSET_ALLOCATION } from '@/enums/AssetAllocation';
import { ASSET_GROUPS, SUBALLOCATION_ORDER } from '@/utils/constants';

// Third party
import get from 'lodash/get';

// Namespaces
import * as ProposalNS from '@/modules/proposal/Proposal';
import * as ProposalElementsNS from '@/modules/proposal/ProposalElements';

// Utils
import { generateLocalizedString, LOCALE } from '@/utils/i18n';
import { HOLDING_TYPES } from '@/utils/portfolios';
import { createOtherSubAllocation, isCashAllocation, isEquityAllocation, isFixedIncomeAllocation } from '@/utils/allocations';

// Types
import AggregatedAssetDetails from '@/types/aggregated-asset-details';
import AllocationDetails from '@/types/allocation-details';
import Alt from '@/types/models/alt';
import Dictionary from '@/types/dictionary';
import Fund from '@/types/fund';
import HoldingTypeAllocation from '@/types/holding-type-allocation';
import InvestmentItem from '@/types/investment-item';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';
import LocalizedString from '@/types/localized-string';
import LocalizedStyleDetails from '@/types/localized-style-details';
import Portfolio from '@/types/portfolio';
import ProposalOrientation from '@/types/proposal-orientation';
import SeriesData from '@/types/series-data';
import Stock from '@/types/stock';

/**
 * Constants for Asset allocation pagination calculations
 */
export const ASSET_ALLOCATION_PAGINATION = {
  TABLE: {
    Portrait: { fractionPerRow: 0.037, baseFraction: 0.16, bottomPaddingRows: 3 },
    Landscape: { fractionPerRow: 0.03, baseFraction: 0.16, bottomPaddingRows: 3 },
  },
  DONUT: {
    Portrait: { fractionPerItem: 0.025, headerSpace: 0.08 },
    Landscape: { fractionPerItem: 0.02, headerSpace: 0.08 },
  },
} as const;

/**
 * Calculate the maximum number of rows that can fit on a single page for Asset allocation tables
 */
export function calculateAssetAllocationMaxRowsPerPage(orientation: ProposalOrientation): number {
  const config = ASSET_ALLOCATION_PAGINATION.TABLE[orientation];
  return Math.max(1, Math.floor((1.0 - config.baseFraction) / config.fractionPerRow) - config.bottomPaddingRows);
}

/**
 * Calculate the number of additional pages needed for Asset allocation table overflow
 */
export function calculateAssetAllocationAdditionalPages(totalRowCount: number, orientation: ProposalOrientation): number {
  const maxRowsPerPage = calculateAssetAllocationMaxRowsPerPage(orientation);
  return totalRowCount > maxRowsPerPage ? Math.ceil(totalRowCount / maxRowsPerPage) - 1 : 0;
}

/**
 * Calculate the number of additional pages needed for Asset allocation donut overflow
 */
export function calculateAssetAllocationDonutAdditionalPages(dataPointCount: number, assignedPageFraction: number, orientation: ProposalOrientation): number {
  const config = ASSET_ALLOCATION_PAGINATION.DONUT[orientation];
  const totalSpaceNeeded = config.headerSpace + dataPointCount * config.fractionPerItem;

  if (totalSpaceNeeded > assignedPageFraction) {
    const overflowSpace = totalSpaceNeeded - assignedPageFraction;
    const spacePerFullPage = 1.0 - config.headerSpace;
    return Math.ceil(overflowSpace / spacePerFullPage);
  }

  return 0;
}

/**
 * Calculate the maximum number of donut items that can fit on a single page
 */
export function calculateAssetAllocationDonutMaxItemsPerPage(orientation: ProposalOrientation): number {
  const safeOrientation = orientation || 'Portrait';
  const config = ASSET_ALLOCATION_PAGINATION.DONUT[safeOrientation];
  return Math.max(1, Math.floor((1.0 - config.headerSpace) / config.fractionPerItem));
}

/**
 * Check if a section contains Asset allocation data
 */
export function hasAssetAllocationData(sectionElement: any): boolean {
  const checkElement = (element: any): boolean => {
    if (element.type === 'DATA' && element.daoObject && element.DAO?.type === 'ASSET_ALLOCATION') {
      return true;
    }
    if (element.type === 'LAYOUT' && element.elements) {
      return element.elements.some((el: any) => checkElement(el));
    }
    return false;
  };

  return checkElement(sectionElement.layout);
}

/**
 * Get the row count from Asset allocation data
 */
export function getAssetAllocationRowCount(sectionElement: ProposalElementsNS.ProposalSectionElement): number | null {
  const findAssetAllocationDAO = (element: any): any => {
    if (element.type === 'DATA' && element.daoObject && element.DAO?.type === 'ASSET_ALLOCATION') {
      return element.daoObject;
    }
    if (element.type === 'LAYOUT' && element.elements) {
      for (const el of element.elements) {
        const dao = findAssetAllocationDAO(el);
        if (dao) return dao;
      }
    }
    return null;
  };

  const dao = findAssetAllocationDAO(sectionElement.layout);
  if (!dao) return null;

  // Use getFullRowCount to avoid triggering paging logic during page fraction calculation
  if (dao.getFullRowCount) return dao.getFullRowCount();

  // Fallback to getTableData for non-AssetAllocationDAO instances
  const tableData = dao.getTableData?.();
  return tableData?.rows?.length || null;
}

/**
 * Check if a section contains Asset allocation donut view
 */
export function hasAssetAllocationDonutData(sectionElement: ProposalElementsNS.ProposalSectionElement): boolean {
  const checkElement = (element: any): boolean => {
    if (element.type === 'DATA' && element.daoObject && element.DAO?.type === 'ASSET_ALLOCATION' && element.dataView?.type === ProposalNS.DataView.Donut) {
      return true;
    }
    if (element.type === 'LAYOUT' && element.elements) {
      return element.elements.some((el: any) => checkElement(el));
    }
    return false;
  };

  return checkElement(sectionElement.layout);
}

/**
 * Get the donut data from Asset allocation donut section for space estimation
 */
export function getAssetAllocationDonutData(sectionElement: ProposalElementsNS.ProposalSectionElement): any | null {
  const findAssetAllocationDonutDAO = (element: any): any => {
    if (element.type === 'DATA' && element.daoObject && element.DAO?.type === 'ASSET_ALLOCATION' && element.dataView?.type === ProposalNS.DataView.Donut) {
      return element.daoObject;
    }
    if (element.type === 'LAYOUT' && element.elements) {
      for (const el of element.elements) {
        const dao = findAssetAllocationDonutDAO(el);
        if (dao) return dao;
      }
    }
    return null;
  };

  const dao = findAssetAllocationDonutDAO(sectionElement.layout);
  return dao?.getDonutData?.(0) || null;
}

export type AllocationsDictionary = Dictionary<{ name: LocalizedString; weight: number; subAllocations?: LocalizedAllocationDetails[] }>;

/**
 * Sort sub-allocations based on predefined order
 * @param items Array of items to sort
 * @param getNameFn Function to extract name from item
 * @returns Sorted array
 */
const sortBySubAllocationOrder = <T>(items: T[], getName: (item: T) => string): T[] => {
  // Create a copy to avoid mutating the original array
  const sortedItems = [...items];

  sortedItems.sort((a, b) => {
    const aName = getName(a);
    const bName = getName(b);
    const aIndex = SUBALLOCATION_ORDER.indexOf(aName);
    const bIndex = SUBALLOCATION_ORDER.indexOf(bName);

    // If both items are not in the predefined order, maintain their original order
    if (aIndex === -1 && bIndex === -1) return 0;
    // If only a is not in the list, put it at the end
    if (aIndex === -1) return 1;
    // If only b is not in the list, put it at the end
    if (bIndex === -1) return -1;
    // Otherwise sort by the predefined order
    return aIndex - bIndex;
  });

  return sortedItems;
};

export const sortSubAllocations = (allocations: LocalizedAllocationDetails[]): LocalizedAllocationDetails[] =>
  sortBySubAllocationOrder(allocations, item => item.name[LOCALE.EN_CA] || '');

/**
 * Sort sub-allocations for donut view - sorts all sub-allocations together as one group
 * Combines duplicate sub-allocations by summing their weights
 * @param subAllocationData The series data containing sub-allocation names and values
 * @returns Sorted series data with unique sub-allocations based on the predefined sub-allocation order
 */
export const sortSubAllocationsForDonut = (subAllocationData: SeriesData[]): SeriesData[] => {
  const uniqueSubAllocations = subAllocationData.reduce((acc, item) => {
    const existingItem = acc.find(accItem => accItem.name === item.name);

    if (existingItem) {
      existingItem.y = (existingItem.y || 0) + (item.y || 0);
      if (!existingItem.key && item.key) {
        existingItem.key = item.key;
      }
    } else {
      // Add new unique sub-allocation
      acc.push({ ...item });
    }

    return acc;
  }, [] as SeriesData[]);

  return sortBySubAllocationOrder(uniqueSubAllocations, item => item.name);
};

/**
 * Sort function -> largest weight to smallest weight
 */
const descendingWeight = (a: { weight: number }, b: { weight: number }) => b.weight - a.weight;

export const formatAllocations = (allocations: AllocationsDictionary, total = 0): AllocationDetails[] =>
  Object.keys(allocations)
    .map(key => ({
      name: allocations[key].name[LOCALE.EN_CA] || '',
      weight: total ? (allocations[key].weight / total) * 100 : 0,
    }))
    .sort(descendingWeight);

const formatLocalizedAllocations = (allocations: AllocationsDictionary, total = 0): LocalizedAllocationDetails[] | LocalizedStyleDetails[] =>
  Object.keys(allocations)
    .map(key => {
      const base = {
        name: allocations[key].name,
        weight: total ? (allocations[key].weight / total) * 100 : 0,
      };

      if (allocations[key].subAllocations) {
        const subTotal = allocations[key].subAllocations.reduce((sum, sa) => sum + sa.weight, 0);

        const normalizedSubs = allocations[key].subAllocations.map(sa => ({
          ...sa,
          weight: subTotal ? (sa.weight / subTotal) * 100 : 0,
        }));

        return { ...base, subAllocations: normalizedSubs };
      }

      return base;
    })
    .sort(descendingWeight);

/**
 * Calculate weighted average of assets or holdings
 * @param {Portfolio} portfolio Portfolio to be calculated
 * @param {InvestmentItem[]} data Funds and Stocks data for the portfolio items.
 * @param {string} path the path of the property to calculate
 * @return {Object} Dictionary of names and average weights (and types for holdings)
 */
export const calculateAllocationAtPath = (portfolio: Portfolio, data: InvestmentItem[], path: string) => {
  const allocations: AllocationsDictionary = {};
  const holdingsHaveSubAllocation = data.some(d => d.allocations?.asset?.some(a => a.subAllocations));
  const pathIsAssetAllocation = path === 'allocations.asset';

  portfolio.holdings.forEach(holding => {
    const obj = data.find(d => d._id === holding.original.id);
    if (!obj) return;

    const allocationItems = get(obj, path, []);

    allocationItems.forEach((item: AllocationDetails | LocalizedAllocationDetails) => {
      if (!item?.name) return;

      const name = normalizeItemName(item.name);
      const localizedName = name[LOCALE.EN_CA];
      if (!localizedName) return;

      const value = holding.dollars * (item.weight / 100);

      // We only handle the suballocations if there are some and the currently processed allocation is 'asset'
      if (pathIsAssetAllocation && holdingsHaveSubAllocation) {
        handleSubAllocations(allocations, item, localizedName, value);
      } else {
        allocations[localizedName] = {
          ...allocations[localizedName],
          name,
          weight: (allocations[localizedName]?.weight || 0) + value,
        };
      }
    });
  });

  return allocations;
};

const normalizeItemName = (name: string | LocalizedString): LocalizedString =>
  typeof name === 'string' ? { [LOCALE.EN_CA]: name, [LOCALE.FR_CA]: name } : name;

const handleSubAllocations = (
  allocations: AllocationsDictionary,
  item: AllocationDetails | LocalizedAllocationDetails,
  localizedName: string,
  value: number,
) => {
  if (!item.subAllocations) {
    handleMissingSubAllocations(allocations, localizedName, value);
  } else {
    const name = normalizeItemName(item.name);
    allocations[localizedName] = {
      ...allocations[localizedName],
      name,
      weight: (allocations[localizedName]?.weight || 0) + value,
    };
    processExistingSubAllocations(allocations, item, localizedName, value);
  }
};

const handleMissingSubAllocations = (allocations: AllocationsDictionary, localizedName: string, value: number) => {
  const isEquityLevelOne = isEquityAllocation(localizedName);
  const isFixedIncomeLevelOne = isFixedIncomeAllocation(localizedName);
  const isCashLevelOne = isCashAllocation(localizedName);

  if (isEquityLevelOne) {
    const equityKey = PORTFOLIO_ASSET_ALLOCATION.EQ;

    // Initialize Equity allocation if needed
    if (!allocations[equityKey]) {
      allocations[equityKey] = {
        name: generateLocalizedString(`Comparison.Table.AssetDetails.${DETAILED_ASSET_ALLOCATION.EQ}`),
        weight: 0,
        subAllocations: [],
      };
    }

    allocations[equityKey].weight += value;

    addToOtherSubAllocation(allocations[equityKey], value);

    // Remove the original allocation if it was not the original Equity allocation
    if (localizedName !== equityKey) delete allocations[localizedName];
  } else if (isFixedIncomeLevelOne) {
    const fixedIncomeKey = localizedName;

    // Initialize Fixed Income allocation if needed
    if (!allocations[fixedIncomeKey]) {
      allocations[fixedIncomeKey] = {
        name: generateLocalizedString('Comparison.Table.AssetDetails.FixedIncome'),
        weight: 0,
        subAllocations: [],
      };
    }

    allocations[fixedIncomeKey].weight += value;

    addToOtherSubAllocation(allocations[fixedIncomeKey], value);
  } else if (isCashLevelOne) {
    const cashKey = PORTFOLIO_ASSET_ALLOCATION.CASH;

    // Initialize Cash allocation if needed
    if (!allocations[cashKey]) {
      allocations[cashKey] = {
        name: generateLocalizedString(`Comparison.Table.AssetDetails.${DETAILED_ASSET_ALLOCATION.CASH}`),
        weight: 0,
        subAllocations: [],
      };
    }

    allocations[cashKey].weight += value;

    addToOtherSubAllocation(allocations[cashKey], value);
  }
};

const addToOtherSubAllocation = (allocation: AllocationsDictionary[string], value: number): void => {
  if (!allocation.subAllocations) allocation.subAllocations = [];

  const otherIndex = allocation.subAllocations.findIndex(sub => sub.name[LOCALE.EN_CA] === 'Other');

  if (otherIndex !== -1) {
    allocation.subAllocations[otherIndex].weight += value;
  } else {
    allocation.subAllocations.push(createOtherSubAllocation(value));
  }
};

const processExistingSubAllocations = (
  allocations: AllocationsDictionary,
  item: AllocationDetails | LocalizedAllocationDetails,
  localizedName: string,
  value: number,
) => {
  const normalizedSubAllocations = item.subAllocations!.map(subItem => ({
    ...subItem,
    name: normalizeItemName(subItem.name),
  })) as LocalizedAllocationDetails[];

  if (allocations[localizedName].subAllocations) {
    // Merge with existing sub-allocations
    const existingSubMap = createSubAllocationMap(allocations[localizedName].subAllocations!);
    updateSubAllocationMap(existingSubMap, normalizedSubAllocations, value);
    allocations[localizedName].subAllocations = Object.values(existingSubMap);
  } else {
    // Create new sub-allocations
    allocations[localizedName].subAllocations = normalizedSubAllocations.map(sub => ({
      ...sub,
      weight: value * (sub.weight / 100),
    }));
  }
};

const createSubAllocationMap = (subAllocations: LocalizedAllocationDetails[]) => {
  const map: { [subName: string]: LocalizedAllocationDetails } = {};
  subAllocations.forEach(sub => {
    const subName = sub.name[LOCALE.EN_CA];
    if (subName) map[subName] = sub;
  });
  return map;
};

const updateSubAllocationMap = (
  existingSubMap: { [subName: string]: LocalizedAllocationDetails },
  newSubAllocations: LocalizedAllocationDetails[],
  value: number,
) => {
  newSubAllocations.forEach(newSub => {
    const subName = newSub.name[LOCALE.EN_CA];
    if (!subName) return;

    const scaledWeight = value * (newSub.weight / 100);

    if (existingSubMap[subName]) {
      existingSubMap[subName].weight += scaledWeight;
    } else {
      existingSubMap[subName] = { ...newSub, weight: scaledWeight };
    }
  });
};

export const getAssetAllocations = (portfolio: Portfolio, data: InvestmentItem[]) => {
  const allocations = calculateAllocationAtPath(portfolio, data, 'allocations.asset');
  return formatLocalizedAllocations(allocations, portfolio.size);
};

export const getSectorAllocations = (portfolio: Portfolio, data: InvestmentItem[]) => {
  const allocations = calculateAllocationAtPath(portfolio, data, 'allocations.sector');
  return formatLocalizedAllocations(allocations, portfolio.size);
};

export const getCountryAllocations = (portfolio: Portfolio, data: InvestmentItem[], path: string) => {
  const allocations = calculateAllocationAtPath(portfolio, data, path);
  return formatAllocations(allocations, portfolio.size);
};

export const getHoldingAllocations = (portfolio: Portfolio, data: InvestmentItem[]) => {
  const allocations = calculateAllocationAtPath(portfolio, data, 'holdings.list');
  return formatAllocations(allocations, portfolio.size);
};

export const getAllocations = (portfolio: Portfolio, data: InvestmentItem[], allocation: string) => {
  const allocations = calculateAllocationAtPath(portfolio, data, `allocations.${allocation}`);

  // Roll up to the total to what is currently allocated
  const values = Object.values(allocations);
  const size = values.length && values.reduce((sum, a) => sum + a.weight, 0);
  return formatAllocations(allocations, size);
};

/**
 * Aggregate the asset details into Equity, Fixed Income and Other.
 * @param assetDetails Complete asset details of the portfolio.
 */
export const aggregateAssetDetails = (assetDetails: LocalizedAllocationDetails[]): AggregatedAssetDetails => {
  const allocations = {
    alternativeInvestment: 0,
    equity: 0,
    fixedIncome: 0,
    other: 0,
  };

  if (assetDetails.length === 0) {
    return allocations;
  }

  const equityIsPresent = assetDetails.some(assetDetail => assetDetail.name[LOCALE.EN_CA] === DETAILED_ASSET_ALLOCATION.EQ);

  assetDetails.forEach(asset => {
    const { name, weight } = asset;

    switch (name[LOCALE.EN_CA]) {
      case DETAILED_ASSET_ALLOCATION.CANADIAN_EQ:
      case DETAILED_ASSET_ALLOCATION.US_EQ:
      case DETAILED_ASSET_ALLOCATION.FOREIGN_EQ: {
        if (!equityIsPresent) {
          allocations.equity += weight;
        }
        break;
      }
      case DETAILED_ASSET_ALLOCATION.EQ: {
        allocations.equity += weight;
        break;
      }
      case DETAILED_ASSET_ALLOCATION.FIXED_INCOME_US:
      case DETAILED_ASSET_ALLOCATION.FIXED_INCOME_CAN:
      case DETAILED_ASSET_ALLOCATION.FIXED_INCOME_FOREIGN:
      case DETAILED_ASSET_ALLOCATION.FIXED_INCOME:
        allocations.fixedIncome += weight;
        break;
      default:
        allocations.other += weight;
        break;
    }
  });

  return allocations;
};

export const getLocalizedAllocations = (portfolio: Portfolio, data: InvestmentItem[], path: string) => {
  const allocations = calculateAllocationAtPath(portfolio, data, path);

  // Roll up to the total to what is currently allocated
  const values = Object.values(allocations);
  const size = values.length && values.reduce((sum, a) => sum + a.weight, 0);

  return formatLocalizedAllocations(allocations, size);
};

type FundAllocation = {
  original: string;
  name: LocalizedString;
  weight: number;
  percentageGrowth: {
    '1Y': number;
    '3Y': number;
    '5Y': number;
    '10Y': number;
  };
  mer: number;
  classification: LocalizedString;
  assetGroup: string;
};

export const getFundAllocations = (portfolio: Portfolio, funds: Fund[], stocks: Stock[], alts: Alt[]) => {
  const dictionary: Dictionary<FundAllocation> = {};

  for (const holding of portfolio.holdings) {
    if (holding.original.type === HOLDING_TYPES.FUND) {
      const fund = funds.find(f => f._id === holding.original.id);

      if (!fund) {
        continue;
      }

      const { fundPortfolio } = fund;

      if (fundPortfolio && fundPortfolio.holdings && fundPortfolio.holdings.funds && fundPortfolio.holdings.funds.length) {
        for (const fundAllocation of fundPortfolio.holdings.funds) {
          const item = dictionary[fundAllocation.original] || {
            ...fundAllocation,
            weight: 0,
          };

          item.weight += fundAllocation.weight * (holding.weight / 100);
          dictionary[fundAllocation.original] = item;
        }
      } else {
        const item = dictionary[fund._id] || {
          original: fund._id,
          percentageGrowth: {
            '1Y': fund.performance.percentGrowth['1Y'],
            '3Y': fund.performance.percentGrowth['3Y'],
            '5Y': fund.performance.percentGrowth['5Y'],
            '10Y': fund.performance.percentGrowth['10Y'],
            INC: fund.performance.percentGrowth.INC,
          },
          name: fund.names?.full,
          weight: 0,
          mer: fund.mer,
          classification: fund.classification,
          assetGroup: fund.assetGroup,
        };

        item.weight += holding.weight;
        dictionary[fund._id] = item;
      }
    }

    if (holding.original.type === HOLDING_TYPES.STOCK) {
      const stock = stocks.find(s => s._id === holding.original.id);

      if (!stock) {
        continue;
      }

      const item = dictionary[stock._id] || {
        original: stock._id,
        percentageGrowth: {
          '1Y': stock.performance.percentGrowth['1Y'],
          '3Y': stock.performance.percentGrowth['3Y'],
          '5Y': stock.performance.percentGrowth['5Y'],
          '10Y': stock.performance.percentGrowth['10Y'],
          INC: stock.performance.percentGrowth.INC,
        },
        name: { 'en-CA': stock.companyName },
        weight: 0,
        mer: 0,
        classification: stock.classification,
        assetGroup: stock.assetGroup,
      };

      item.weight += holding.weight;
      dictionary[stock._id] = item;
    }

    if (holding.original.type === HOLDING_TYPES.ALT) {
      const alt = alts?.find(s => s._id === holding.original.id);
      if (!alt) continue;
      const item = dictionary[alt._id] || {
        original: alt._id,
        percentageGrowth: {
          '1Y': alt.performance.percentGrowth?.['1Y'] ?? null,
          '3Y': alt.performance.percentGrowth?.['3Y'] ?? null,
          '5Y': alt.performance.percentGrowth?.['5Y'] ?? null,
          '10Y': alt.performance.percentGrowth?.['10Y'] ?? null,
          INC: alt.performance.percentGrowth?.INC ?? null,
        },
        name: alt.names?.full,
        weight: 0,
        mer: 0,
        classification: alt.classification,
        assetGroup: ASSET_GROUPS.ALTERNATIVE_INVESTMENT,
      };
      item.weight += holding.weight;
      dictionary[alt._id] = item;
    }
  }

  return Object.values(dictionary);
};

/**
 * Return aggregated item type data
 * @param portfolio Portfolio containing the funds to aggregate
 * @param data Funds and Stocks reference data store
 */
export const getHoldingTypes = (portfolio: Portfolio, data: InvestmentItem[]) =>
  portfolio.holdings.reduce(
    (acc, holding) => {
      const item = data.find(d => d._id === holding.original.id);

      let key;
      if (holding.original.type === HOLDING_TYPES.STOCK) {
        key = 'EQ';
      } else {
        const allocationType = HOLDING_TYPE_ALLOCATION.find(type => type.dataKey === (item && item.assetType && item.assetType['en-CA']));
        key = (allocationType && allocationType.key) || 'OTHER';
      }

      acc[key].total += holding.weight;
      acc[key].holdings.push(holding);

      return acc;
    },
    HOLDING_TYPE_ALLOCATION.reduce((acc, type) => {
      acc[type.key] = { total: 0, holdings: [] };
      return acc;
    }, {} as HoldingTypeAllocation),
  );
